import { z } from 'zod';

const mitigationBandSchema = z.object({
  lowerBound: z.number(),
  upperBound: z.number(),
});

const dimensionSchema = z.object({
  aggregateAvg: z.number().nullable(),
  multiplier: z.number(),
  band: mitigationBandSchema.nullable(),
});

const controlTypeContributionSchema = z.object({
  type: z.string().nullable(),
  count: z.number(),
  avgEffectiveness: z.number(),
  likelihoodWeight: z.number(),
  impactWeight: z.number(),
});

export const RiskScoreBreakdownIncompleteReason = {
  MissingInherentAssessment: 'missing_inherent_assessment',
  NoControlsLinked: 'no_controls_linked',
  AllControlsFilteredOut: 'all_controls_filtered_out',
  ControlsWithoutTestResults: 'controls_without_test_results',
} as const;

export type RiskScoreBreakdownIncompleteReason =
  (typeof RiskScoreBreakdownIncompleteReason)[keyof typeof RiskScoreBreakdownIncompleteReason];

const incompleteSchema = z.object({
  status: z.literal('incomplete'),
  reason: z.enum([
    RiskScoreBreakdownIncompleteReason.MissingInherentAssessment,
    RiskScoreBreakdownIncompleteReason.NoControlsLinked,
    RiskScoreBreakdownIncompleteReason.AllControlsFilteredOut,
    RiskScoreBreakdownIncompleteReason.ControlsWithoutTestResults,
  ]),
  details: z.string().optional(),
});

/**
 * 'none' keeps the calculated value's fractional precision (RSP-7273).
 * This enum also validates the `roundingMode` recorded inside a persisted
 * breakdown, so it must accept a mode before any calculator writes it
 * (RSP-7274) — data-layer validates every upserted row against this schema,
 * so a mode it rejects fails the whole batch and no score persists at all.
 */
export const roundingModeSchema = z.enum(['nearest', 'up', 'down', 'none']);

export type RoundingMode = z.infer<typeof roundingModeSchema>;

const scoreTripleSchema = z.object({
  likelihood: z.number(),
  impact: z.number(),
  score: z.number(),
});

const completeSchema = z.object({
  status: z.literal('complete'),
  inherent: scoreTripleSchema,
  residual: scoreTripleSchema,
  controlTypes: z.array(controlTypeContributionSchema),
  likelihood: dimensionSchema,
  impact: dimensionSchema,
  /**
   * Rounding mode applied to residual likelihood/impact (RSP-5817).
   * Optional for backward compatibility — existing persisted breakdowns
   * without this field still parse. Always populated for new breakdowns
   * emitted by models that read a `roundingMode` config.
   */
  roundingMode: roundingModeSchema.optional(),
});

/**
 * One child risk's contribution to a parent (tier 1/2) score. `inherent` /
 * `residual` are the child's stored `risk_score` values at the time of the
 * rollup — `null` when the child had no score in that block (no risk_score
 * row, or a falsy score, which the aggregator treats as unscored).
 */
const childContributionSchema = z.object({
  riskId: z.string().uuid(),
  inherent: scoreTripleSchema.nullable(),
  residual: scoreTripleSchema.nullable(),
});

export const RiskScoreAggregationMethod = {
  Average: 'average',
  WorstCase: 'worst_case',
} as const;

export type RiskScoreAggregationMethod =
  (typeof RiskScoreAggregationMethod)[keyof typeof RiskScoreAggregationMethod];

const blockCountsSchema = z.object({
  included: z.number().int().nonnegative(),
  excluded: z.number().int().nonnegative(),
});

/**
 * A parent (tier 1/2) row's breakdown (RSP-7285): the child risks it was
 * rolled up from, with their stored values at the time, so the explanation
 * is reproducible from the row alone. One variant whether or not the
 * parent's blocks resolved — when a block is `null`, the children and the
 * counts are the explanation. Child titles are deliberately not stored; the
 * browser resolves `riskId` to a title at render time.
 */
const aggregatedFieldsSchema = z.object({
  status: z.literal('aggregated'),
  method: z.enum([
    RiskScoreAggregationMethod.Average,
    RiskScoreAggregationMethod.WorstCase,
  ]),
  /**
   * The tier rollup is an unweighted mean; control weighting applies inside
   * a leaf, never to children. A literal so a future weighting scheme is a
   * schema change, not a silent reinterpretation of old rows.
   */
  weighting: z.literal('none'),
  /** Mode applied to the averaged dimensions. Absent when the model has no configurable rollup rounding. */
  roundingMode: roundingModeSchema.optional(),
  children: z.object({
    total: z.number().int().nonnegative(),
    inherent: blockCountsSchema,
    residual: blockCountsSchema,
  }),
  contributions: z.array(childContributionSchema),
  /** The parent's own values after rounding — what the row stores. `null` when the block did not resolve. */
  inherent: scoreTripleSchema.nullable(),
  residual: scoreTripleSchema.nullable(),
  /** Only for `worst_case`: which child each block was lifted from. */
  worstCase: z
    .object({
      inherentRiskId: z.string().uuid().nullable(),
      residualRiskId: z.string().uuid().nullable(),
    })
    .optional(),
});

/**
 * Cross-field invariants the rollup maintains by construction. The data-layer
 * upsert validates a `BreakdownData` through this schema, and until now it
 * checked each field's shape in isolation — so a mismatched child count, a
 * worst-case source naming a child that is not in the list, or a scored parent
 * with no children would all have persisted, and the popover would have
 * explained the score with the wrong numbers.
 *
 * One invariant is deliberately absent: a block's `included` count is NOT
 * required to equal the number of non-null contributions for that block. The
 * count follows the aggregator's own inclusion test (a truthy score), while a
 * contribution needs all three of likelihood, impact and score, so a child
 * carrying a score with a null dimension is legitimately counted-in with a
 * null contribution. Requiring equality would make the data-layer reject the
 * whole upsert batch and fail a recalculation over a row it should accept.
 */
const refineAggregated = (
  breakdown: z.infer<typeof aggregatedFieldsSchema>,
  ctx: z.RefinementCtx
): void => {
  if (breakdown.children.total !== breakdown.contributions.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['children', 'total'],
      message: `children.total (${breakdown.children.total}) must equal the number of contributions (${breakdown.contributions.length})`,
    });
  }

  for (const block of ['inherent', 'residual'] as const) {
    const { included, excluded } = breakdown.children[block];
    if (included + excluded !== breakdown.children.total) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['children', block],
        message: `children.${block} counts (${included} included + ${excluded} excluded) must sum to children.total (${breakdown.children.total})`,
      });
    }
  }

  const isWorstCase = breakdown.method === RiskScoreAggregationMethod.WorstCase;
  if (isWorstCase && !breakdown.worstCase) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['worstCase'],
      message: 'worstCase is required when method is "worst_case"',
    });
  }
  if (!isWorstCase && breakdown.worstCase) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['worstCase'],
      message: `worstCase must be absent when method is "${breakdown.method}"`,
    });
  }

  // A `null` id is the block that did not resolve, which is legitimate; an id
  // naming a child outside the contributions is not — the popover resolves it
  // against that list, so it would name nothing. A non-null id must also point
  // at a child that has a value for that block, or the narrative attributes
  // the parent's score to a child whose own column reads "not scored";
  // `buildAggregatedBreakdownData` drops the id with the block for that reason.
  const contributions = new Map(
    breakdown.contributions.map((contribution) => [
      contribution.riskId,
      contribution,
    ])
  );
  const worstCaseBlocks = [
    { key: 'inherentRiskId', block: 'inherent' },
    { key: 'residualRiskId', block: 'residual' },
  ] as const;
  for (const { key, block } of worstCaseBlocks) {
    const riskId = breakdown.worstCase?.[key];
    if (riskId == null) {
      continue;
    }
    const contribution = contributions.get(riskId);
    if (!contribution) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['worstCase', key],
        message: `worstCase.${key} (${riskId}) is not among the contributions`,
      });
      continue;
    }
    if (contribution[block] === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['worstCase', key],
        message: `worstCase.${key} (${riskId}) names a child with no ${block} score to lift`,
      });
    }
  }

  if (breakdown.contributions.length === 0) {
    for (const block of ['inherent', 'residual'] as const) {
      if (breakdown[block] !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [block],
          message: `${block} must be null when there are no contributing children`,
        });
      }
    }
  }
};

export const riskScoreBreakdownSchema = z
  .discriminatedUnion('status', [
    completeSchema,
    incompleteSchema,
    aggregatedFieldsSchema,
  ])
  // Zod 3's discriminated union takes plain objects only, so the aggregated
  // cross-field checks hang off the union rather than off its member. They run
  // only after the member itself parses, so an unknown `status` still fails
  // with the union's own error.
  .superRefine((breakdown, ctx) => {
    if (breakdown.status === 'aggregated') {
      refineAggregated(breakdown, ctx);
    }
  });

export type RiskScoreBreakdown = z.infer<typeof riskScoreBreakdownSchema>;

export type RiskScoreBreakdownComplete = z.infer<typeof completeSchema>;
export type RiskScoreBreakdownIncomplete = z.infer<typeof incompleteSchema>;
export type RiskScoreBreakdownAggregated = z.infer<
  typeof aggregatedFieldsSchema
>;
export type RiskScoreBreakdownControlTypeContribution = z.infer<
  typeof controlTypeContributionSchema
>;
export type RiskScoreBreakdownChildContribution = z.infer<
  typeof childContributionSchema
>;
